// The full layer shader (WGSL codegen, template, uniform layout, colormap
// and noise/arc SDF libraries) is generated by the Rust engine
// (crates/graph/src/{wgsl,shader_template,colormaps_wgsl}.rs); TS resolves
// value slots per frame and owns GPU device plumbing.
import { compileLayerShaderWasm, packLayerUniformsWasm, packLights, shapeSigmaWasm, coverageRowAlignEpsilonWasm, sdfDistanceDensityWasm as sdfDistanceDensity, sdfFeatherWidthWasm as sdfFeatherWidth } from 'fluveria-engine-wasm';
import { resolveValueSlotValue, type ArcSlot, type ValueSlot } from './shaderSlots.js';
import { getNodeDataRecord, TEXT_LAYOUT_NODE_TYPES, type GraphNode, type GraphEdge, type ParamStore } from '../graph/nodeDefs.js';
import { normalizeGraphScope, type ParamGraphSlot } from '../graph/paramKeys.js';
import { type SdfTheme } from './sdfTheme.js';
import type { Scene } from '../scene/sceneState.js';
import type { LayoutResult, SceneLayoutContext } from '../scene/layoutEngine.js';
import {
  resolveGeometrySlot,
  resolveMaterialSlot,
  type LayerDef,
  type LayerGraphLibraries,
  type ResolvedGraph,
} from '../scene/layerState.js';
import type { ArcBounds } from '../sdf/arcSdf.js';
import {
  adoptCompiledShapeGraph,
  ensureBundledFontsLoaded,
  getLoadedFontRegistry,
  getLoadedFontRegistryKey,
  resolveBundledFontId,
  type ArcGpuBufferData,
  type ArcWindRowGrid,
} from '../sdf/shapeEngine.js';
import { collectSceneDrawItems, mergeLayerParams, type SceneDrawItem } from './gpuRenderer.js';
import type { GraphRuntimeResolver } from '../graph/graphRuntime.js';
import { DEFAULT_ARC_COVERAGE_MODE, type ArcCoverageMode } from '../sdf/arcCoverageMode.js';
import type { SvgPathShape } from '../graph/shapeAssets.js';
import type { DocumentLight } from '../graph/lights.js';
import { worldScaleOf } from '../scene/layoutMath.js';
import { NO_LOCAL_CLIP, clipInLayer, type ClipBox } from '../scene/clip.js';

// Types and module state

export class WebGPUDiagnosticError extends Error {
  stage: string;
  details: string[];

  constructor(message: string, stage: string, details: string[] = []) {
    super(message);
    this.name = 'WebGPUDiagnosticError';
    this.stage = stage;
    this.details = details;
  }
}

interface CachedPipeline {
  id: number;
  src: string;
  pipeline: GPURenderPipeline;
}

interface ArcGpuBuffers {
  meta: GPUBuffer;
  dist: GPUBuffer;
  bvh: GPUBuffer;
  bvhLeaves: GPUBuffer;
  wind: GPUBuffer;
  windRowOffsets: GPUBuffer;
  windRowIndices: GPUBuffer;
}

interface ArcBufferCacheEntry {
  buffers: ArcGpuBuffers;
  bytes: number;
}

interface DrawResourceCacheEntry {
  resources: DrawResources;
}

interface WebGPUState {
  adapter: GPUAdapter;
  device: GPUDevice;
  context: GPUCanvasContext;
  format: GPUTextureFormat;
  /**
   * The sRGB view of {@link format}, and the format every pipeline targets.
   *
   * The shaders emit linear light -- the engine's working space (its
   * `INVARIANTS.md#colour-is-computed-in-one-working-space`) -- and leave the
   * sRGB encode to the hardware, which only does it for a target whose format
   * carries the `-srgb` suffix. `getPreferredCanvasFormat()` returns the
   * unsuffixed one, so configuring the context with it alone puts working-space
   * values on screen unencoded and the whole document reads dark.
   */
  viewFormat: GPUTextureFormat;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  sceneCache: Map<string, CachedPipeline>;
  sceneCompileCache: Map<string, WGSLCompileResult>;
  standaloneCache: CachedPipeline | null;
  drawResourceCache: Map<string, DrawResourceCacheEntry>;
  // Tier-4 cache: uploaded arc geometry buffers keyed by the composite of arc
  // slot keys. Lets material-only re-renders reuse the heavy geometry buffers
  // instead of rebuilding and re-uploading them every frame.
  arcBufferCache: Map<string, ArcBufferCacheEntry>;
  arcBufferCacheBytes: number;
  frameUniformBuffer: GPUBuffer | null;
  frameUniformBufferBytes: number;
  frameValueBuffer: GPUBuffer | null;
  frameValueBufferBytes: number;
  retiredFrameUploadBuffers: GPUBuffer[];
}

/** GPU arc-buffer cache budget. Bounds live-edit churn, not a correctness limit. */
const ARC_GPU_BUFFER_CACHE_MAX_BYTES = 256 * 1024 * 1024;
const SCENE_COMPILE_CACHE_MAX_ENTRIES = 64;
const DRAW_RESOURCE_CACHE_MAX_ENTRIES = 512;
const FRAME_UPLOAD_INITIAL_BYTES = 64 * 1024;
const FRAME_UPLOAD_GROWTH_ALIGNMENT = 4096;

interface WGSLCompileResult {
  src: string;
  arcSlots: ArcSlot[];
  valueSlots: ValueSlot[];
  /** The module reads the document's lamps after its value slots (`drawValueData`). */
  usesLights: boolean;
  layoutBounds: ArcBounds | null;
  contourBounds: ArcBounds | null;
}

type StableKeyValue = string | number | boolean | null | readonly StableKeyValue[] | { readonly [key: string]: StableKeyValue };

interface DrawResources {
  bindGroup: GPUBindGroup;
}

interface GpuBufferSlice {
  buffer: GPUBuffer;
  offset: number;
  size: number;
}

interface FrameUploadData {
  uniformData: Float32Array;
  valueData: Float32Array;
}

interface FrameUploadPlan {
  uniformBuffer: GPUBuffer;
  valueBuffer: GPUBuffer;
  uniformBytes: Uint8Array;
  valueBytes: Uint8Array;
  uniformAlignment: number;
  valueAlignment: number;
  uniformOffset: number;
  valueOffset: number;
}

interface PreparedSceneDraw {
  pipeline: CachedPipeline;
  resources: DrawResources;
  viewport: { x: number; y: number; w: number; h: number };
}

interface PreparedSceneDrawInput extends FrameUploadData {
  pipeline: CachedPipeline;
  arcSlots: ArcSlot[];
  viewport: { x: number; y: number; w: number; h: number };
}

type DrawBounds = [number, number, number, number];

let gpuState: WebGPUState | null = null;
let nextPipelineId = 1;
let nextGpuBufferId = 1;
const gpuBufferIds = new WeakMap<GPUBuffer, number>();

/**
 * Runtime shape-density guard.
 *
 * Converts screen pixels to shape units. Collapsed or nearly collapsed layer
 * transforms clamp here so exact coverage never receives an infinite footprint.
 */
const PIXELS_PER_SHAPE_UNIT_EPSILON = 1e-6;

function dilateDrawBounds(bounds: DrawBounds, dilationPx: number): DrawBounds {
  if (dilationPx <= 0) return bounds;
  return [
    bounds[0] - dilationPx,
    bounds[1] - dilationPx,
    bounds[2] + dilationPx,
    bounds[3] + dilationPx,
  ];
}

// Shader compilation (engine-backed)

export function compileGraphToWGSL(
  graphId: string | null,
  geometryNodes: GraphNode[],
  geometryEdges: GraphEdge[],
  material: ResolvedGraph | null,
  renderSlot: 'geometry' | 'material',
  colormap: string,
  params: ParamStore,
  aaMode: 'none' | 'fwidth' | 'msaa2x',
  aaWidth: number,
  sdfTheme: SdfTheme,
  arcCoverageMode: ArcCoverageMode = DEFAULT_ARC_COVERAGE_MODE,
  layerSize: [number, number] = [1, 1],
  pixelsPerShapeUnit: number = 1,
  pixelsPerShapeUnitX: number = pixelsPerShapeUnit,
  pixelsPerShapeUnitY: number = pixelsPerShapeUnit,
  windRowGrid?: ArcWindRowGrid,
  shapes: Record<string, SvgPathShape> = {},
): WGSLCompileResult {
  // Fonts referenced by Text nodes load asynchronously; until they arrive the
  // engine produces empty leaves and the compile cache key changes once
  // loading completes (the font-loaded event triggers a re-render).
  const fontIds = new Set<string>();
  for (const node of geometryNodes) {
    if (TEXT_LAYOUT_NODE_TYPES.has(node.type)) fontIds.add(String(getNodeDataRecord(node).font ?? ''));
  }
  if (fontIds.size > 0) {
    void ensureBundledFontsLoaded([...fontIds].map(id => resolveBundledFontId(id)));
  }

  const result = compileLayerShaderWasm({
    graphId: graphId ?? undefined,
    geometry: { nodes: geometryNodes, edges: geometryEdges },
    material: material ? { nodes: material.nodes, edges: material.edges } : null,
    renderSlot,
    colormap,
    params,
    aaMode,
    aaWidth,
    sdfTheme,
    arcCoverageMode,
    layerSize,
    pixelsPerShapeUnit,
    pixelsPerShapeUnitX,
    pixelsPerShapeUnitY,
    windRowGrid,
    shapes,
    fonts: getLoadedFontRegistry(),
  });
  const src = result.src();
  const valueSlots = result.valueSlots() as ValueSlot[];
  const arcSlotInfos = result.arcSlots() as Array<{ key: string; index: number }>;
  const layoutBoundsArr = result.layoutBounds();
  const contourBoundsArr = result.contourBounds();
  const usesLights = result.usesLights();
  const shapeResult = result.takeShapeResult();
  result.free();

  const adopted = shapeResult ? adoptCompiledShapeGraph(shapeResult) : null;
  const arcSlots: ArcSlot[] = arcSlotInfos.map(slot => ({
    key: slot.key,
    index: slot.index,
    shapeData: adopted?.query ?? undefined,
    contourBounds: adopted?.contourBounds,
  }));
  const toBounds = (values: Float32Array): ArcBounds => (
    { minX: values[0], minY: values[1], maxX: values[2], maxY: values[3] }
  );

  return {
    src,
    arcSlots,
    valueSlots,
    usesLights,
    layoutBounds: toBounds(layoutBoundsArr),
    contourBounds: toBounds(contourBoundsArr),
  };
}

function stableKey(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableKey).join(',')}]`;
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    return `${view.constructor.name}:${view.byteLength}`;
  }
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableKey(record[key])}`)
    .join(',')}}`;
}

function graphStructureKey(nodes: GraphNode[], edges: GraphEdge[], sizeMode?: ResolvedGraph['sizeMode']): string {
  return stableKey({
    nodes: nodes.map(node => ({
      id: node.id,
      type: node.type,
      data: node.data,
    })),
    edges: edges.map(edge => ({
      fromNode: edge.fromNode,
      fromPort: edge.fromPort,
      toNode: edge.toNode,
      toPort: edge.toPort,
    })),
    sizeMode,
  });
}

function legacyScopedParamPrefix(graphId: string | null | undefined, slot: ParamGraphSlot): string | null {
  if (!graphId || graphId.includes(':')) return null;
  return `param:layer:${graphId}:${slot}:`;
}

function scopedParamSignature(graphId: string | null | undefined, slot: ParamGraphSlot, params: ParamStore): StableKeyValue[] {
  const scopedPrefix = `param:${normalizeGraphScope(graphId, slot)}:`;
  const legacyPrefix = legacyScopedParamPrefix(graphId, slot);
  return Object.entries(params)
    .filter(([key]) => key.startsWith(scopedPrefix) || (legacyPrefix ? key.startsWith(legacyPrefix) : false))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => [key, value as StableKeyValue]);
}

function sdfThemeShaderKey(sdfTheme: SdfTheme): StableKeyValue {
  return {
    fillOutside: sdfTheme.fillOutside,
    fillInside: sdfTheme.fillInside,
    strokeColor: sdfTheme.strokeColor,
    isoColor: sdfTheme.isoColor,
  };
}

function windRowGridKey(windRowGrid: ArcWindRowGrid | undefined): StableKeyValue {
  return windRowGrid
    ? { originY: windRowGrid.originY, rowSize: windRowGrid.rowSize }
    : null;
}

function sceneCompileCacheKey(
  graphId: string,
  geometryNodes: GraphNode[],
  geometryEdges: GraphEdge[],
  geometrySizeMode: ResolvedGraph['sizeMode'] | undefined,
  material: ResolvedGraph | null,
  renderSlot: 'geometry' | 'material',
  colormap: string,
  params: ParamStore,
  aaMode: 'none' | 'fwidth' | 'msaa2x',
  aaWidth: number,
  sdfTheme: SdfTheme,
  arcCoverageMode: ArcCoverageMode,
  layerSize: [number, number],
  pixelsPerShapeUnit: number,
  pixelsPerShapeUnitX: number,
  pixelsPerShapeUnitY: number,
  windRowGrid: ArcWindRowGrid | undefined,
  shapes: Record<string, SvgPathShape>,
): string {
  return stableKey({
    graphId,
    geometry: graphStructureKey(geometryNodes, geometryEdges, geometrySizeMode),
    geometryParams: scopedParamSignature(graphId, 'geometry', params),
    // Shape compilation reads the loaded-font registry; a font arriving
    // changes the compiled text leaves with no graph/param change.
    fonts: getLoadedFontRegistryKey(),
    shapes,
    material: material ? graphStructureKey(material.nodes, material.edges, material.sizeMode) : null,
    renderSlot,
    colormap,
    aaMode,
    aaWidth,
    sdfTheme: sdfThemeShaderKey(sdfTheme),
    arcCoverageMode,
    layerSize,
    pixelsPerShapeUnit,
    pixelsPerShapeUnitX,
    pixelsPerShapeUnitY,
    windRowGrid: windRowGridKey(windRowGrid),
  });
}

function getOrCompileSceneGraphToWGSL(
  state: WebGPUState,
  graphId: string,
  geometryNodes: GraphNode[],
  geometryEdges: GraphEdge[],
  geometrySizeMode: ResolvedGraph['sizeMode'] | undefined,
  material: ResolvedGraph | null,
  renderSlot: 'geometry' | 'material',
  colormap: string,
  params: ParamStore,
  aaMode: 'none' | 'fwidth' | 'msaa2x',
  aaWidth: number,
  sdfTheme: SdfTheme,
  arcCoverageMode: ArcCoverageMode,
  layerSize: [number, number],
  pixelsPerShapeUnit: number,
  pixelsPerShapeUnitX: number,
  pixelsPerShapeUnitY: number,
  windRowGrid: ArcWindRowGrid | undefined,
  shapes: Record<string, SvgPathShape>,
): WGSLCompileResult {
  const key = sceneCompileCacheKey(
    graphId,
    geometryNodes,
    geometryEdges,
    geometrySizeMode,
    material,
    renderSlot,
    colormap,
    params,
    aaMode,
    aaWidth,
    sdfTheme,
    arcCoverageMode,
    layerSize,
    pixelsPerShapeUnit,
    pixelsPerShapeUnitX,
    pixelsPerShapeUnitY,
    windRowGrid,
    shapes,
  );
  const cached = state.sceneCompileCache.get(key);
  if (cached) {
    state.sceneCompileCache.delete(key);
    state.sceneCompileCache.set(key, cached);
    return cached;
  }

  const compiled = compileGraphToWGSL(
    graphId,
    geometryNodes,
    geometryEdges,
    material,
    renderSlot,
    colormap,
    params,
    aaMode,
    aaWidth,
    sdfTheme,
    arcCoverageMode,
    layerSize,
    pixelsPerShapeUnit,
    pixelsPerShapeUnitX,
    pixelsPerShapeUnitY,
    windRowGrid,
    shapes,
  );
  state.sceneCompileCache.set(key, compiled);
  while (state.sceneCompileCache.size > SCENE_COMPILE_CACHE_MAX_ENTRIES) {
    const oldestKey = state.sceneCompileCache.keys().next().value;
    if (oldestKey === undefined) break;
    state.sceneCompileCache.delete(oldestKey);
  }
  return compiled;
}

// Resource and state helpers

function destroyWebGPUState(state: WebGPUState | null): void {
  if (!state) return;
  state.sceneCache.clear();
  state.sceneCompileCache.clear();
  state.standaloneCache = null;
  state.drawResourceCache.clear();
  for (const entry of state.arcBufferCache.values()) destroyArcGpuBuffers(entry.buffers);
  state.arcBufferCache.clear();
  state.arcBufferCacheBytes = 0;
  destroyFrameUploadBuffers(state);
}

function destroyArcGpuBuffers(buffers: ArcGpuBuffers): void {
  buffers.meta.destroy();
  buffers.dist.destroy();
  buffers.bvh.destroy();
  buffers.bvhLeaves.destroy();
  buffers.wind.destroy();
  buffers.windRowOffsets.destroy();
  buffers.windRowIndices.destroy();
}

function destroyFrameUploadBuffers(state: WebGPUState): void {
  state.frameUniformBuffer?.destroy();
  state.frameUniformBuffer = null;
  state.frameUniformBufferBytes = 0;
  state.frameValueBuffer?.destroy();
  state.frameValueBuffer = null;
  state.frameValueBufferBytes = 0;
  for (const buffer of state.retiredFrameUploadBuffers) buffer.destroy();
  state.retiredFrameUploadBuffers = [];
}

function resizeWebGPUState(state: WebGPUState, width: number, height: number): void {
  if (state.width === width && state.height === height && state.canvas.width === width && state.canvas.height === height) return;
  state.width = width;
  state.height = height;
  state.canvas.width = width;
  state.canvas.height = height;
  state.context.configure({
    device: state.device,
    format: state.format,
    viewFormats: [state.viewFormat],
    alphaMode: 'premultiplied',
  });
}

async function getOrCreateWebGPU(gpu: GPU | undefined, canvas: HTMLCanvasElement, width: number, height: number, deferResize = false): Promise<WebGPUState> {
  if (gpuState && gpuState.canvas === canvas) {
    if (!deferResize) resizeWebGPUState(gpuState, width, height);
    return gpuState;
  }

  destroyWebGPUState(gpuState);
  if (!gpu) {
    throw new WebGPUDiagnosticError('WebGPU is not available in this browser.', 'adapter');
  }

  const adapter = await gpu.requestAdapter();
  if (!adapter) throw new WebGPUDiagnosticError('No compatible WebGPU adapter was found.', 'adapter');
  const device = await adapter.requestDevice();
  const context = canvas.getContext('webgpu');
  if (!context) throw new WebGPUDiagnosticError('Failed to create a WebGPU canvas context.', 'context');

  const format = gpu.getPreferredCanvasFormat();
  const viewFormat = `${format}-srgb` as GPUTextureFormat;
  canvas.width = width;
  canvas.height = height;
  context.configure({
    device,
    format,
    viewFormats: [viewFormat],
    alphaMode: 'premultiplied',
  });

  gpuState = {
    adapter,
    device,
    context,
    format,
    viewFormat,
    canvas,
    width,
    height,
    sceneCache: new Map(),
    sceneCompileCache: new Map(),
    standaloneCache: null,
    drawResourceCache: new Map(),
    arcBufferCache: new Map(),
    arcBufferCacheBytes: 0,
    frameUniformBuffer: null,
    frameUniformBufferBytes: 0,
    frameValueBuffer: null,
    frameValueBufferBytes: 0,
    retiredFrameUploadBuffers: [],
  };
  return gpuState;
}

async function createPipeline(state: WebGPUState, src: string): Promise<CachedPipeline> {
  const module = state.device.createShaderModule({ code: src });
  const info = await module.getCompilationInfo();
  const errors = info.messages
    .filter(msg => msg.type === 'error')
    .map(msg => `${msg.lineNum}:${msg.linePos} ${msg.message}`);
  if (errors.length > 0) {
    throw new WebGPUDiagnosticError('WGSL compilation failed.', 'shader', errors);
  }

  const entries: GPUBindGroupLayoutEntry[] = [{
    binding: 0,
    visibility: GPUShaderStage.FRAGMENT,
    buffer: { type: 'uniform' },
  }];
  for (let i = 1; i <= 8; i++) {
    entries.push({
      binding: i,
      visibility: GPUShaderStage.FRAGMENT,
      buffer: { type: 'read-only-storage' },
    });
  }

  const bindGroupLayout = state.device.createBindGroupLayout({ entries });
  const pipelineLayout = state.device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] });
  const pipeline = await state.device.createRenderPipelineAsync({
    layout: pipelineLayout,
    vertex: {
      module,
      entryPoint: 'vsMain',
    },
    fragment: {
      module,
      entryPoint: 'fsMain',
      targets: [{
        format: state.viewFormat,
        blend: {
          color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
          alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
        },
      }],
    },
    primitive: {
      topology: 'triangle-strip',
    },
  });
  return { id: nextPipelineId++, src, pipeline };
}

async function getOrCreatePipeline(state: WebGPUState, cacheKey: string, src: string, standalone: boolean): Promise<CachedPipeline> {
  if (standalone) {
    if (state.standaloneCache && state.standaloneCache.src === src) {
      return state.standaloneCache;
    }
    state.standaloneCache = await createPipeline(state, src);
    return state.standaloneCache;
  }

  const cached = state.sceneCache.get(cacheKey);
  if (cached && cached.src === src) return cached;
  const next = await createPipeline(state, src);
  state.sceneCache.set(cacheKey, next);
  return next;
}

function createBuffer(device: GPUDevice, data: ArrayBuffer | ArrayBufferView, usage: GPUBufferUsageFlags): GPUBuffer {
  const byteLength = data.byteLength;
  const buffer = device.createBuffer({
    size: Math.max(16, Math.ceil(byteLength / 4) * 4),
    usage,
    mappedAtCreation: true,
  });
  const mapped = new Uint8Array(buffer.getMappedRange());
  const bytes = data instanceof ArrayBuffer
    ? new Uint8Array(data)
    : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  mapped.set(bytes);
  buffer.unmap();
  return buffer;
}

function alignBytes(value: number, alignment: number): number {
  return Math.ceil(value / alignment) * alignment;
}

function growFrameUploadBuffer(state: WebGPUState, kind: 'uniform' | 'value', minBytes: number, usage: GPUBufferUsageFlags): GPUBuffer {
  const nextBytes = Math.max(FRAME_UPLOAD_INITIAL_BYTES, alignBytes(minBytes, FRAME_UPLOAD_GROWTH_ALIGNMENT));
  const nextBuffer = state.device.createBuffer({ size: nextBytes, usage });
  state.drawResourceCache.clear();
  if (kind === 'uniform') {
    if (state.frameUniformBuffer) state.retiredFrameUploadBuffers.push(state.frameUniformBuffer);
    state.frameUniformBuffer = nextBuffer;
    state.frameUniformBufferBytes = nextBytes;
  } else {
    if (state.frameValueBuffer) state.retiredFrameUploadBuffers.push(state.frameValueBuffer);
    state.frameValueBuffer = nextBuffer;
    state.frameValueBufferBytes = nextBytes;
  }
  return nextBuffer;
}

function ensureFrameUploadBuffer(state: WebGPUState, kind: 'uniform' | 'value', minBytes: number, usage: GPUBufferUsageFlags): GPUBuffer {
  if (kind === 'uniform') {
    if (state.frameUniformBuffer && state.frameUniformBufferBytes >= minBytes) return state.frameUniformBuffer;
    return growFrameUploadBuffer(state, kind, minBytes, usage);
  }
  if (state.frameValueBuffer && state.frameValueBufferBytes >= minBytes) return state.frameValueBuffer;
  return growFrameUploadBuffer(state, kind, minBytes, usage);
}

function beginFrameUpload(state: WebGPUState, draws: FrameUploadData[]): FrameUploadPlan {
  const uniformAlignment = Math.max(4, state.device.limits.minUniformBufferOffsetAlignment ?? 256);
  const valueAlignment = Math.max(4, state.device.limits.minStorageBufferOffsetAlignment ?? 256);
  const uniformTotalBytes = draws.reduce((total, draw) => total + alignBytes(draw.uniformData.byteLength, uniformAlignment), 0);
  const valueTotalBytes = draws.reduce((total, draw) => total + alignBytes(draw.valueData.byteLength, valueAlignment), 0);
  return {
    uniformBuffer: ensureFrameUploadBuffer(state, 'uniform', uniformTotalBytes, GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST),
    valueBuffer: ensureFrameUploadBuffer(state, 'value', valueTotalBytes, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    uniformBytes: new Uint8Array(uniformTotalBytes),
    valueBytes: new Uint8Array(valueTotalBytes),
    uniformAlignment,
    valueAlignment,
    uniformOffset: 0,
    valueOffset: 0,
  };
}

function stageFrameUploadData(upload: FrameUploadPlan, kind: 'uniform' | 'value', data: Float32Array): GpuBufferSlice {
  const alignment = kind === 'uniform' ? upload.uniformAlignment : upload.valueAlignment;
  const buffer = kind === 'uniform' ? upload.uniformBuffer : upload.valueBuffer;
  const offset = kind === 'uniform' ? upload.uniformOffset : upload.valueOffset;
  const size = data.byteLength;
  const bytes = new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength);
  if (kind === 'uniform') {
    upload.uniformBytes.set(bytes, offset);
  } else {
    upload.valueBytes.set(bytes, offset);
  }
  if (kind === 'uniform') {
    upload.uniformOffset += alignBytes(size, alignment);
  } else {
    upload.valueOffset += alignBytes(size, alignment);
  }
  return { buffer, offset, size };
}

function commitFrameUpload(state: WebGPUState, upload: FrameUploadPlan): void {
  if (upload.uniformOffset > 0) {
    const bytes = new Uint8Array(upload.uniformBytes.buffer as ArrayBuffer, 0, upload.uniformOffset);
    state.device.queue.writeBuffer(upload.uniformBuffer, 0, bytes);
  }
  if (upload.valueOffset > 0) {
    const bytes = new Uint8Array(upload.valueBytes.buffer as ArrayBuffer, 0, upload.valueOffset);
    state.device.queue.writeBuffer(upload.valueBuffer, 0, bytes);
  }
}

function gpuBufferId(buffer: GPUBuffer): number {
  const existing = gpuBufferIds.get(buffer);
  if (existing !== undefined) return existing;
  const id = nextGpuBufferId++;
  gpuBufferIds.set(buffer, id);
  return id;
}

function gpuBufferSliceCacheKey(slice: GpuBufferSlice): string {
  return `${gpuBufferId(slice.buffer)}:${slice.offset}:${slice.size}`;
}

function destroyRetiredFrameUploadBuffersAfterSubmit(state: WebGPUState): void {
  if (state.retiredFrameUploadBuffers.length === 0) return;
  const retired = state.retiredFrameUploadBuffers.splice(0);
  void state.device.queue.onSubmittedWorkDone()
    .then(() => {
      for (const buffer of retired) buffer.destroy();
    })
    .catch(() => {
      for (const buffer of retired) buffer.destroy();
    });
}

function buildValueBufferData(valueSlots: ValueSlot[], params: ParamStore, graphId: string | null): Float32Array {
  const data = new Float32Array(Math.max(1, valueSlots.length) * 4);
  for (const slot of valueSlots) {
    const value = resolveValueSlotValue(slot, params, graphId);
    const offset = slot.index * 4;
    data[offset] = value[0];
    data[offset + 1] = value[1];
    data[offset + 2] = value[2];
    data[offset + 3] = value[3];
  }
  return data;
}

/**
 * A draw's value buffer: its slots, and after them the document's lamps
 * when its module reads them -- where the module's `illuminance` looks
 * (`LIGHTS_BASE`, one past the slots), as the engine's own plan appends them
 * (render/src/gpu_plan.rs). A module that reads no lamp gets its slots alone.
 */
export function drawValueData(
  compiled: Pick<WGSLCompileResult, 'valueSlots' | 'usesLights'>,
  params: ParamStore,
  graphId: string | null,
  lights: readonly DocumentLight[],
): Float32Array {
  const slots = buildValueBufferData(compiled.valueSlots, params, graphId);
  if (!compiled.usesLights) return slots;
  const lamps = packLights(lights);
  const data = new Float32Array(slots.length + lamps.length);
  data.set(slots, 0);
  data.set(lamps, slots.length);
  return data;
}

export interface RenderThemeUniform {
  mode: number;
  resolvedTheme: number;
}

const DEFAULT_RENDER_THEME: RenderThemeUniform = { mode: 0, resolvedTheme: 0 };

function boundsVec(bounds: ArcBounds | null | undefined): [number, number, number, number] {
  return bounds ? [bounds.minX, bounds.minY, bounds.maxX, bounds.maxY] : [0, 0, 0, 0];
}

export function makeUniformData(localSize: [number, number], canvasH: number, m: [number, number, number, number], translate: [number, number], fit: [number, number], worldScale: [number, number], shapeSigma: [number, number], drawBoundsLocal: [number, number, number, number], density: number, featherWidth: number, shapeOrigin: [number, number], renderTheme: RenderThemeUniform, layoutBounds: ArcBounds | null = null, contourBounds: ArcBounds | null = null, clipLocal: ClipBox = NO_LOCAL_CLIP): Float32Array {
  // Packed by the engine so the layout has one source with the WGSL
  // Uniforms struct in shader_template.rs; the frame upload sizes each slice
  // from what comes back, so the block's size is the engine's too.
  //
  // The two bounds are per-draw data. They used to be baked into value slots
  // at compile time, which tied a compiled module to the layer that compiled
  // it; the geometry stage now reads them from here. `clipLocal` is the clip a
  // turned clipping frame above makes, in the layer's own units, which the
  // scissor cannot hold (`scene/clip.ts`); `NO_LOCAL_CLIP` is none.
  return packLayerUniformsWasm({
    localSize, canvasH, m, translate, fit, worldScale, shapeSigma, drawBoundsLocal,
    density, featherWidth, shapeOrigin, renderTheme,
    layoutBounds: boundsVec(layoutBounds), contourBounds: boundsVec(contourBounds),
    clipLocal,
  });
}
function shapeOriginForCompiled(compiled: Pick<WGSLCompileResult, 'layoutBounds'>, localSize: [number, number], sizeMode: ResolvedGraph['sizeMode'] | undefined): [number, number] {
  if (sizeMode?.kind === 'layoutBounds' && compiled.layoutBounds) {
    return [compiled.layoutBounds.minX, compiled.layoutBounds.minY];
  }
  return [-localSize[0] * 0.5, -localSize[1] * 0.5];
}

function windRowGridForLayer(
  layout: SceneDrawItem['layout'],
  localSize: [number, number],
  sizeMode: ResolvedGraph['sizeMode'] | undefined,
  pixelsPerShapeUnit: number,
): ArcWindRowGrid | undefined {
  if (sizeMode?.kind === 'layoutBounds') return undefined;
  const rowSize = 1 / Math.max(pixelsPerShapeUnit, PIXELS_PER_SHAPE_UNIT_EPSILON);
  const lfw = layout.toLocal;
  // The engine bakes the same tolerance into the generated shader's
  // row-aligned exact-coverage fast path, so grid eligibility must use it.
  const rowAlignEpsilon = coverageRowAlignEpsilonWasm();
  if (Math.abs(lfw[1]) > rowAlignEpsilon) return undefined;
  if (Math.abs(Math.abs(lfw[5]) - rowSize) > rowAlignEpsilon) return undefined;
  return {
    originY: -localSize[1] * 0.5 + lfw[13],
    rowSize,
  };
}

const EMPTY_ARC_BUFFER_DATA: ArcGpuBufferData = {
  meta: new Uint8Array(80),
  distArcs: new Float32Array(12),
  bvhNodes: new Float32Array(8),
  bvhLeaves: new Uint32Array(1),
  windSegs: new Float32Array(8),
  windRowOffsets: new Uint32Array(2),
  windRowIndices: new Uint32Array(1),
};

function buildArcBufferData(arcSlots: ArcSlot[]): ArcGpuBufferData {
  if (arcSlots.length === 0) return EMPTY_ARC_BUFFER_DATA;
  if (arcSlots.length !== 1 || arcSlots[0].index !== 0) {
    throw new WebGPUDiagnosticError(
      'Arc GPU buffer packing only supports one shape slot.',
      'arc-buffer-pack',
      [
        `slotCount=${arcSlots.length}`,
        `indices=${arcSlots.map(slot => slot.index).join(',')}`,
      ],
    );
  }
  return arcSlots[0].shapeData?.gpuBufferData ?? EMPTY_ARC_BUFFER_DATA;
}

export function arcBufferCacheKey(arcSlots: ArcSlot[]): string {
  // The combined buffers depend on the ordered set of slots: `index` drives the
  // meta/offset layout, and the shape's own key names the geometry.
  //
  // The slot key alone does not: since the engine emits one shader module per
  // material and shares it between layers, a slot names its place in the module
  // ("shape"), not what is drawn there. Keying on it alone gave every arc layer
  // in the document one buffer -- the first one built -- so all text and every
  // icon drew whatever geometry happened to arrive first, stretched to their own
  // bounds, which reads on screen as a filled rectangle.
  return arcSlots
    .map(slot => `${slot.index}:${slot.key}:${slot.shapeData?.key ?? 'empty'}`)
    .join('|');
}

function getOrCreateArcBuffers(state: WebGPUState, arcSlots: ArcSlot[]): ArcGpuBuffers {
  const key = arcBufferCacheKey(arcSlots);
  const cached = state.arcBufferCache.get(key);
  if (cached) {
    // Refresh LRU recency.
    state.arcBufferCache.delete(key);
    state.arcBufferCache.set(key, cached);
    return cached.buffers;
  }

  const arcData = buildArcBufferData(arcSlots);
  const buffers: ArcGpuBuffers = {
    meta: createBuffer(state.device, arcData.meta, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    dist: createBuffer(state.device, arcData.distArcs, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    bvh: createBuffer(state.device, arcData.bvhNodes, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    bvhLeaves: createBuffer(state.device, arcData.bvhLeaves, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    wind: createBuffer(state.device, arcData.windSegs, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    windRowOffsets: createBuffer(state.device, arcData.windRowOffsets, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
    windRowIndices: createBuffer(state.device, arcData.windRowIndices, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST),
  };
  const bytes = arcData.meta.byteLength
    + arcData.distArcs.byteLength
    + arcData.bvhNodes.byteLength
    + arcData.bvhLeaves.byteLength
    + arcData.windSegs.byteLength
    + arcData.windRowOffsets.byteLength
    + arcData.windRowIndices.byteLength;

  state.arcBufferCache.set(key, { buffers, bytes });
  state.arcBufferCacheBytes += bytes;
  return buffers;
}

// Eviction runs after submit (not during draw preparation) so a buffer
// referenced by the current frame's bind groups is never destroyed before the
// command buffer is submitted.
function evictArcBuffers(state: WebGPUState): void {
  let evicted = false;
  while (state.arcBufferCacheBytes > ARC_GPU_BUFFER_CACHE_MAX_BYTES && state.arcBufferCache.size > 1) {
    const oldestKey = state.arcBufferCache.keys().next().value;
    if (oldestKey === undefined) break;
    const oldest = state.arcBufferCache.get(oldestKey);
    if (!oldest) break;
    state.arcBufferCache.delete(oldestKey);
    state.arcBufferCacheBytes -= oldest.bytes;
    destroyArcGpuBuffers(oldest.buffers);
    evicted = true;
  }
  if (evicted) state.drawResourceCache.clear();
}

function createDrawResources(
  state: WebGPUState,
  pipeline: CachedPipeline,
  uniformSlice: GpuBufferSlice,
  arcSlots: ArcSlot[],
  valueSlice: GpuBufferSlice,
): DrawResources {
  const arcKey = arcBufferCacheKey(arcSlots);
  const arc = getOrCreateArcBuffers(state, arcSlots);
  const cacheKey = [
    pipeline.id,
    gpuBufferSliceCacheKey(uniformSlice),
    gpuBufferSliceCacheKey(valueSlice),
    arcKey,
  ].join('|');
  const cached = state.drawResourceCache.get(cacheKey);
  if (cached) {
    state.drawResourceCache.delete(cacheKey);
    state.drawResourceCache.set(cacheKey, cached);
    return cached.resources;
  }

  const entries: GPUBindGroupEntry[] = [
    { binding: 0, resource: uniformSlice },
    { binding: 1, resource: { buffer: arc.meta } },
    { binding: 2, resource: { buffer: arc.dist } },
    { binding: 3, resource: { buffer: arc.bvh } },
    { binding: 4, resource: { buffer: arc.bvhLeaves } },
    { binding: 5, resource: { buffer: arc.wind } },
    { binding: 6, resource: { buffer: arc.windRowOffsets } },
    { binding: 7, resource: { buffer: arc.windRowIndices } },
    { binding: 8, resource: valueSlice },
  ];

  const resources = {
    bindGroup: state.device.createBindGroup({
      layout: pipeline.pipeline.getBindGroupLayout(0),
      entries,
    }),
  };
  state.drawResourceCache.set(cacheKey, { resources });
  while (state.drawResourceCache.size > DRAW_RESOURCE_CACHE_MAX_ENTRIES) {
    const oldestKey = state.drawResourceCache.keys().next().value;
    if (oldestKey === undefined) break;
    state.drawResourceCache.delete(oldestKey);
  }
  return resources;
}

// Public render entry points

export async function renderWebGPU(
  gpu: GPU | undefined,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  geometryNodes: GraphNode[],
  geometryEdges: GraphEdge[],
  material: ResolvedGraph | null,
  params: ParamStore,
  renderSlot: 'geometry' | 'material',
  colormap: string,
  sdfTheme: SdfTheme,
  aaMode: 'none' | 'fwidth' | 'msaa2x' = 'fwidth',
  aaWidth: number = 0.5,
  graphId: string | null = 'component_main',
  arcCoverageMode: ArcCoverageMode = DEFAULT_ARC_COVERAGE_MODE,
  renderTheme: RenderThemeUniform = DEFAULT_RENDER_THEME,
  shapes: Record<string, SvgPathShape> = {},
  lights: readonly DocumentLight[] = [],
): Promise<void> {
  const state = await getOrCreateWebGPU(gpu, canvas, width, height, true);
  const compiled = compileGraphToWGSL(graphId, geometryNodes, geometryEdges, material, renderSlot, colormap, params, aaMode, aaWidth, sdfTheme, arcCoverageMode, [width, height], 1, 1, 1, undefined, shapes);
  const pipeline = await getOrCreatePipeline(state, 'standalone', compiled.src, true);
  const previewDensity = sdfDistanceDensity(1);
  const featherWidth = sdfFeatherWidth(aaWidth, 1, previewDensity);
  const uniformData = makeUniformData([width, height], height, [1, 0, 0, 1], [0, 0], [1, 1], [1, 1], [1, 1], [0, 0, 0, 0], previewDensity, featherWidth, [-width * 0.5, -height * 0.5], renderTheme, compiled.layoutBounds, compiled.contourBounds);
  const valueData = drawValueData(compiled, params, graphId, lights);
  const upload = beginFrameUpload(state, [{ uniformData, valueData }]);
  const resources = createDrawResources(
    state,
    pipeline,
    stageFrameUploadData(upload, 'uniform', uniformData),
    compiled.arcSlots,
    stageFrameUploadData(upload, 'value', valueData),
  );
  commitFrameUpload(state, upload);

  resizeWebGPUState(state, width, height);
  const encoder = state.device.createCommandEncoder();
  const pass = encoder.beginRenderPass({
    colorAttachments: [{
      view: state.context.getCurrentTexture().createView({ format: state.viewFormat }),
      loadOp: 'clear',
      storeOp: 'store',
      clearValue: {
        r: sdfTheme.clearColor[0],
        g: sdfTheme.clearColor[1],
        b: sdfTheme.clearColor[2],
        a: 1,
      },
    }],
  });
  pass.setPipeline(pipeline.pipeline);
  pass.setViewport(0, 0, width, height, 0, 1);
  pass.setScissorRect(0, 0, width, height);
  pass.setBindGroup(0, resources.bindGroup);
  pass.draw(4);
  pass.end();
  state.device.queue.submit([encoder.finish()]);
  evictArcBuffers(state);
  destroyRetiredFrameUploadBuffersAfterSubmit(state);
}

export async function renderSceneWebGPU(
  gpu: GPU | undefined,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  scene: Scene,
  layoutResult: LayoutResult,
  layoutContext: SceneLayoutContext & { resolveRuntime: GraphRuntimeResolver },
  layerLibrary: Record<string, LayerDef>,
  layerGraphLibraries: LayerGraphLibraries,
  renderSlot: 'geometry' | 'material',
  colormap: string,
  aaMode: 'none' | 'fwidth' | 'msaa2x',
  aaWidth: number,
  arcCoverageMode: ArcCoverageMode,
  sdfTheme: SdfTheme,
  renderTheme: RenderThemeUniform = DEFAULT_RENDER_THEME,
  shapes: Record<string, SvgPathShape> = {},
  lights: readonly DocumentLight[] = [],
): Promise<void> {
  const state = await getOrCreateWebGPU(gpu, canvas, width, height, true);
  const items: SceneDrawItem[] = [];
  collectSceneDrawItems(scene, layoutResult, layoutContext, layerLibrary, scene.rootIds, items, new Set());
  const preparedDrawInputs: PreparedSceneDrawInput[] = [];

  for (const { layerNode, layerDef, layout, runtime, clip, paramOverrides } of items) {
    const resolvedGeometry = resolveGeometrySlot(layerGraphLibraries, layerDef.geometry);
    if (!resolvedGeometry) continue;
    const resolvedMaterial = resolveMaterialSlot(layerGraphLibraries, layerDef.material);
    const mergedParams = mergeLayerParams(layoutContext.params, paramOverrides);

    const aabb = layout.aabb;
    const worldScale = worldScaleOf(layout.fromLocal);
    const baseDrawBounds = runtime?.materialDrawBounds(mergedParams, layout.localBounds, worldScale) ?? [0, 0, 0, 0];
    const dilationPx = runtime?.dynamicDrawBoundsDilationPx?.(renderSlot) ?? 0;
    const drawBounds = dilateDrawBounds(baseDrawBounds, dilationPx);
    let x0 = Math.max(0, Math.floor(aabb.x + drawBounds[0]));
    let y0 = Math.max(0, Math.floor(aabb.y + drawBounds[1]));
    let x1 = Math.min(width, Math.ceil(aabb.x + aabb.w + drawBounds[2]));
    let y1 = Math.min(height, Math.ceil(aabb.y + aabb.h + drawBounds[3]));
    const scissor = clip.scissor;
    if (scissor) {
      x0 = Math.max(x0, Math.floor(scissor.x0));
      y0 = Math.max(y0, Math.floor(scissor.y0));
      x1 = Math.min(x1, Math.ceil(scissor.x1));
      y1 = Math.min(y1, Math.ceil(scissor.y1));
    }
    const x = x0;
    const y = y0;
    const w = x1 - x0;
    const h = y1 - y0;
    if (w <= 0 || h <= 0) continue;

    const localW = layout.localBounds.w;
    const localH = layout.localBounds.h;
    const lfw = layout.toLocal;
    const [worldScaleX, worldScaleY] = worldScale;

    let fitX = 1;
    let fitY = 1;
    if (layerNode.fit !== 'fill' && localW > 0 && localH > 0) {
      const ratio = localW / localH;
      if (layerNode.fit === 'contain') {
        if (ratio > 1) fitX = ratio; else fitY = 1 / ratio;
      } else {
        if (ratio > 1) fitY = 1 / ratio; else fitX = ratio;
      }
    }
    const pixelsPerShapeUnit = Math.max(Math.min(worldScaleX, worldScaleY), PIXELS_PER_SHAPE_UNIT_EPSILON);
    const density = sdfDistanceDensity(pixelsPerShapeUnit);
    const windRowGrid = windRowGridForLayer(layout, [localW, localH], resolvedGeometry.sizeMode, pixelsPerShapeUnit);
    const compiled = getOrCompileSceneGraphToWGSL(
      state,
      layerNode.layerId,
      resolvedGeometry.nodes,
      resolvedGeometry.edges,
      resolvedGeometry.sizeMode,
      resolvedMaterial,
      renderSlot,
      colormap,
      mergedParams,
      aaMode,
      aaWidth,
      sdfTheme,
      arcCoverageMode,
      [localW, localH],
      pixelsPerShapeUnit,
      worldScaleX,
      worldScaleY,
      windRowGrid,
      shapes,
    );
    const pipeline = await getOrCreatePipeline(state, layerNode.layerId, compiled.src, false);

    // Per side, and each side by its own axis -- the same four divisions the
    // engine's own painter makes (`scene/src/layer_paint.rs`, `local_x`/
    // `local_y`), so the two renderers cut the same sides. Collapsing them to
    // one number gave a bleed declared on the left a skirt on the right too.
    // `materialDrawBounds` answers in screen pixels; the uniform wants layer units.
    const localX = (offset: number) => (worldScaleX > 0 ? offset / worldScaleX : offset);
    const localY = (offset: number) => (worldScaleY > 0 ? offset / worldScaleY : offset);
    const drawBoundsLocal: [number, number, number, number] = [
      localX(drawBounds[0]),
      localY(drawBounds[1]),
      localX(drawBounds[2]),
      localY(drawBounds[3]),
    ];
    // Singular values, not the column norms `worldScale` already holds: the two
    // agree only while the layer's JᵀJ is diagonal. The engine owns the
    // derivation because sigma_min is a difference of nearly equal numbers.
    const shapeSigmaArr = shapeSigmaWasm(lfw[0], lfw[1], lfw[4], lfw[5]);
    const shapeSigma: [number, number] = [shapeSigmaArr[0], shapeSigmaArr[1]];
    const shapeOrigin = shapeOriginForCompiled(compiled, [localW, localH], resolvedGeometry.sizeMode);
    const featherWidth = sdfFeatherWidth(aaWidth, pixelsPerShapeUnit, density);
    const uniformData = makeUniformData([localW, localH], height, [lfw[0], lfw[4], lfw[1], lfw[5]], [lfw[12], lfw[13]], [fitX, fitY], [worldScaleX, worldScaleY], shapeSigma, drawBoundsLocal, density, featherWidth, shapeOrigin, renderTheme, compiled.layoutBounds, compiled.contourBounds, clipInLayer(clip, lfw));
    const valueData = drawValueData(compiled, mergedParams, layerNode.layerId, lights);
    preparedDrawInputs.push({
      pipeline,
      uniformData,
      valueData,
      arcSlots: compiled.arcSlots,
      viewport: { x, y, w, h },
    });
  }

  const preparedDraws: PreparedSceneDraw[] = [];
  let upload: FrameUploadPlan | null = null;
  if (preparedDrawInputs.length > 0) {
    upload = beginFrameUpload(state, preparedDrawInputs);
    for (const draw of preparedDrawInputs) {
      preparedDraws.push({
        pipeline: draw.pipeline,
        resources: createDrawResources(
          state,
          draw.pipeline,
          stageFrameUploadData(upload, 'uniform', draw.uniformData),
          draw.arcSlots,
          stageFrameUploadData(upload, 'value', draw.valueData),
        ),
        viewport: draw.viewport,
      });
    }
  }
  if (upload) commitFrameUpload(state, upload);

  resizeWebGPUState(state, width, height);
  const encoder = state.device.createCommandEncoder();
  const pass = encoder.beginRenderPass({
    colorAttachments: [{
      view: state.context.getCurrentTexture().createView({ format: state.viewFormat }),
      loadOp: 'clear',
      storeOp: 'store',
      clearValue: {
        r: sdfTheme.clearColor[0],
        g: sdfTheme.clearColor[1],
        b: sdfTheme.clearColor[2],
        a: 1,
      },
    }],
  });

  for (const draw of preparedDraws) {
    pass.setPipeline(draw.pipeline.pipeline);
    pass.setViewport(draw.viewport.x, draw.viewport.y, draw.viewport.w, draw.viewport.h, 0, 1);
    pass.setScissorRect(draw.viewport.x, draw.viewport.y, draw.viewport.w, draw.viewport.h);
    pass.setBindGroup(0, draw.resources.bindGroup);
    pass.draw(4);
  }

  pass.end();
  state.device.queue.submit([encoder.finish()]);
  evictArcBuffers(state);
  destroyRetiredFrameUploadBuffersAfterSubmit(state);
}

// Reset and teardown

export function resetWebGPUState(): void {
  destroyWebGPUState(gpuState);
  gpuState = null;
}

export async function waitForWebGPUIdle(): Promise<void> {
  await gpuState?.device.queue.onSubmittedWorkDone();
}
